// ─────────────────────────────────────────────────────────
// Approved business facts — the single source of truth for anything the
// storefront states about Aurora Strings as a company: who we are, how to
// reach us, and what we promise about fulfilment.
//
// RULE: every value in this file must trace to an owner-approved record.
// Anything not yet confirmed belongs in UNCONFIRMED below and MUST NOT be
// rendered as a customer-facing promise until it is verified and moved up.
//
// See docs/aurora-remediation.md ("Unresolved business facts") for the list
// of decisions still outstanding.
// ─────────────────────────────────────────────────────────

/** Legal / parent entity. Aurora Strings is its music brand. */
export const PARENT_COMPANY = {
  name: "Bridgeport Insulated Wire",
  /**
   * 1950 is the founding year of Bridgeport Insulated Wire — the precision
   * wire manufacturer — NOT of the Aurora Strings brand. Aurora's own founding
   * year is not established; see UNCONFIRMED.auroraBrandFoundingYear.
   */
  foundingYear: 1950,
  city: "Bridgeport",
  region: "CT",
  country: "US",
} as const;

export const BRAND = {
  name: "Aurora Strings",
  /** Safe to state: the brand belongs to a manufacturer operating since 1950. */
  heritageLine: `The music brand of ${PARENT_COMPANY.name}, a precision wire manufacturer operating in ${PARENT_COMPANY.city}, ${PARENT_COMPANY.region} since ${PARENT_COMPANY.foundingYear}.`,
} as const;

/** Direct contact details shown in the footer and on /contact. */
export const CONTACT = {
  email: "Jake.stringsbyaurora@gmail.com",
  phone: "203.335.5739",
  phoneHref: "tel:2033355739",
  city: PARENT_COMPANY.city,
  region: PARENT_COMPANY.region,
  /** City + state only. The postal code is disputed — see UNCONFIRMED. */
  addressLine: `${PARENT_COMPANY.city}, ${PARENT_COMPANY.region}`,
} as const;

/**
 * Fulfilment copy. Production, dispatch, and carrier delivery are three
 * different intervals and must never be collapsed into one promise.
 *
 * All four are currently unverified, so `hasApprovedFulfilmentTerms` is false
 * and the storefront shows no timing or shipping-threshold claim at all.
 */
export const FULFILMENT = {
  hasApprovedFulfilmentTerms: false,
  /** Days from order to a stocked set leaving the building. */
  stockedDispatch: null as string | null,
  /** Days to build a made-to-order custom set, before dispatch. */
  customProduction: null as string | null,
  /** Carrier transit time after dispatch. */
  carrierDelivery: null as string | null,
  /** Order value above which shipping is free, and where it applies. */
  freeShippingThreshold: null as { amount: number; region: string } | null,
  /** Return window and conditions for stocked vs custom items. */
  returns: null as { stocked: string; custom: string } | null,
} as const;

/**
 * Facts the site previously asserted that no approved record supports.
 * Listed here so they are visible to the next person rather than silently
 * dropped — and so nobody re-adds them from memory.
 */
export const UNCONFIRMED = {
  auroraBrandFoundingYear:
    "The year the Aurora Strings brand itself launched. The site previously said 1950, which is the parent manufacturer's founding year.",
  postalCode:
    "Footer said 06610, /contact said 06601. Both removed until one is confirmed.",
  supportEmail:
    "/contact previously listed support@aurorastringsonline.com; the footer listed Jake.stringsbyaurora@gmail.com. Only the footer address is used until the support alias is confirmed to exist.",
  supportHours:
    "/contact previously stated Monday-Friday 9am-5pm ET. Not confirmed.",
  shippingAndReturns:
    "Free-shipping threshold, dispatch window, delivery estimate, and return conditions all previously appeared as hardcoded component copy with no approved source.",
  colorCoatingEffects:
    "Any statement about the coating's mass, tone impact, or string-life extension. No test data or specification sheet is available.",
} as const;
